import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react'
import { v4 as uuidv4 } from 'uuid'

interface ChatContextType {
  sessionId: string
  conversationTitle: string
  isLoading: boolean
  isStreaming: boolean
  hasMessages: boolean
  setConversationTitle: (title: string) => void
  setLoadingState: (loading: boolean, streaming: boolean) => void
  setHasMessages: (hasMessages: boolean) => void
  setSessionId: (sessionId: string) => void
  clearChat: () => void
  registerClearChatFunction: (clearFn: () => void) => void
}

const ChatContext = createContext<ChatContextType | undefined>(undefined)

interface ChatProviderProps {
  children: ReactNode
}

export function ChatProvider({ children }: ChatProviderProps) {
  const [sessionId, setSessionId] = useState(() => uuidv4())
  const [conversationTitle, setConversationTitle] = useState('Untitled')
  const [isLoading, setIsLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [hasMessages, setHasMessages] = useState(false)
  
  // Ref to store the actual clear chat function from the chat component
  const clearChatRef = useRef<(() => void) | null>(null)

  const setLoadingState = useCallback((loading: boolean, streaming: boolean) => {
    setIsLoading(loading)
    setIsStreaming(streaming)
  }, [])

  const registerClearChatFunction = useCallback((clearFn: () => void) => {
    clearChatRef.current = clearFn
  }, [])

  const clearChat = useCallback(() => {
    // Call the actual chat clear function if it's registered
    if (clearChatRef.current) {
      clearChatRef.current()
    }
    
    // Reset all chat state
    setConversationTitle('Untitled')
    setIsLoading(false)
    setIsStreaming(false)
    setHasMessages(false)
    // Note: sessionId will be updated by the Chat component when useN8nChat generates a new one
  }, [])

  const value: ChatContextType = {
    sessionId,
    conversationTitle,
    isLoading,
    isStreaming,
    hasMessages,
    setConversationTitle,
    setLoadingState,
    setHasMessages,
    setSessionId,
    clearChat,
    registerClearChatFunction,
  }

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  )
}

export function useChat() {
  const context = useContext(ChatContext)
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider')
  }
  return context
}
